import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ScrollToTop } from '../components/ScrollToTop';
import { PortalHeader, PortalLoading, formatMaintenanceDate } from '../components/PortalComponents';
import { toast } from '../components/ui/sonner';
import axios from 'axios';
import { 
  Wrench, Search, AlertTriangle, Truck, 
  Calendar, CheckCircle, Clock, Send, MessageCircle, Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'Pendiente', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Clock },
  in_progress: { label: 'En proceso', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Wrench },
  resolved: { label: 'Resuelto', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
  closed: { label: 'Cerrado', color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30', icon: CheckCircle }
};

const TYPE_CONFIG = {
  maintenance: { label: 'Mantenimiento', icon: Wrench, description: 'Mantenimiento preventivo o correctivo' },
  revision: { label: 'Revisión', icon: Search, description: 'Revisión general del equipo' },
  incident: { label: 'Incidencia', icon: AlertTriangle, description: 'Problema o avería' },
  removal: { label: 'Retirada', icon: Truck, description: 'Solicitar retirada del equipo' }
};

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.color}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
      {config.label}
    </span>
  );
}

function RequestTypeCard({ type, typeKey, selected, onClick }) {
  const Icon = type.icon;
  
  return (
    <button
      onClick={() => onClick(typeKey)}
      className={`p-4 rounded-xl border text-left transition-all duration-300 ${
        selected 
          ? 'border-white/40 bg-white/10 shadow-lg' 
          : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
      }`}
    >
      <Icon className={`w-6 h-6 mb-2 ${selected ? 'text-white' : 'text-zinc-500'}`} />
      <p className={`text-sm font-medium ${selected ? 'text-white' : 'text-zinc-300'}`}>{type.label}</p>
      <p className="text-xs text-zinc-500 mt-1">{type.description}</p>
    </button>
  );
}

export default function MaintenancePage() {
  const [tickets, setTickets] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const fetchTickets = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tickets`, { withCredentials: true });
      setTickets(res.data);
    } catch (err) {
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedType) {
      toast.error('Por favor, selecciona un tipo de solicitud');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/tickets`, {
        type: selectedType,
        description: description || `Solicitud de ${TYPE_CONFIG[selectedType]?.label}`
      }, { withCredentials: true });
      
      toast.success('Solicitud enviada correctamente');
      setShowForm(false);
      setSelectedType('');
      setDescription('');
      fetchTickets();
    } catch (err) {
      toast.error('Error al enviar la solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  const handleContactWhatsApp = () => {
    const message = encodeURIComponent('Hola, necesito ayuda con mi equipo Manklar.');
    window.open(`https://wa.me/34623243967?text=${message}`, '_blank');
  };

  // Separate active and resolved tickets
  const activeTickets = tickets.filter(t => ['pending', 'in_progress'].includes(t.status));
  const resolvedTickets = tickets.filter(t => ['resolved', 'closed'].includes(t.status));

  if (loading) {
    return <PortalLoading />;
  }

  return (
    <div className="min-h-screen bg-[#030303]">
      <PortalHeader title="Mantenimiento" />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-light text-white font-['Outfit']">Mantenimiento</h1>
            <p className="text-sm text-zinc-500 mt-1 font-['Manrope']">Solicita asistencia técnica para tu equipo</p>
          </div>
          {!showForm && (
            <Button 
              onClick={() => setShowForm(true)}
              className="bg-white text-black hover:bg-zinc-200 rounded-xl font-semibold shadow-[0_0_20px_rgba(255,255,255,0.1)]"
              data-testid="new-request-btn"
            >
              <Wrench className="w-4 h-4 mr-2" />
              Nueva solicitud
            </Button>
          )}
        </div>

        {/* Info Box */}
        <div className="mb-6 p-4 rounded-xl bg-blue-500/[0.08] border border-blue-500/20">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-400 font-medium mb-1 font-['Outfit']">Información sobre mantenimiento</p>
              <ul className="text-xs text-zinc-400 space-y-1 font-['Manrope']">
                <li>• El mantenimiento preventivo <span className="text-white font-medium">anual</span> está incluido en tu cuota mensual</li>
                <li>• Si solicitas un mantenimiento adicional antes de cumplir el año: <span className="text-white font-medium">59€</span></li>
                <li>• Las incidencias y averías están cubiertas por la Garantía Manklar</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Request Form */}
        {showForm && (
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-white font-['Outfit']">Nueva solicitud</h3>
              <button 
                onClick={() => setShowForm(false)}
                className="text-zinc-500 hover:text-white transition-colors font-['Manrope']"
              >
                Cancelar
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <p className="text-sm text-zinc-400 mb-3 font-['Manrope']">¿Qué tipo de solicitud necesitas?</p>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(TYPE_CONFIG).map(([key, type]) => (
                    <RequestTypeCard 
                      key={key}
                      typeKey={key}
                      type={type}
                      selected={selectedType === key}
                      onClick={setSelectedType}
                    />
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm text-zinc-400 mb-2 font-['Manrope']">
                  Descripción (opcional)
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  placeholder="Describe el problema o la solicitud..."
                  className="w-full px-4 py-3 rounded-xl bg-white/[0.03] border border-white/10 text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/20 transition-colors font-['Manrope']"
                />
              </div>

              <Button 
                type="submit"
                disabled={submitting}
                className="w-full h-12 bg-white text-black hover:bg-zinc-200 rounded-xl font-semibold"
              >
                {submitting ? (
                  <>Enviando...</>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Enviar solicitud
                  </>
                )}
              </Button>
            </form>
          </div>
        )}

        {/* Active Tickets */}
        <div className="mb-8">
          <h3 className="text-xs uppercase tracking-wider text-zinc-500 mb-4 font-['Manrope']">Solicitudes activas</h3>
          {activeTickets.length === 0 ? (
            <div className="text-center py-8 rounded-2xl bg-white/[0.02] border border-white/10">
              <Wrench className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 font-['Manrope']">No tienes solicitudes activas</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activeTickets.map(ticket => (
                <div key={ticket.id} className="p-5 rounded-2xl bg-white/[0.02] border border-white/10">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-sm text-sky-400">{ticket.ticket_number}</span>
                        <span className="text-xs text-zinc-600">•</span>
                        <span className="text-xs text-zinc-500">{TYPE_CONFIG[ticket.type]?.label}</span>
                      </div>
                      <p className="text-sm text-zinc-300 font-['Manrope']">{ticket.description}</p>
                    </div>
                    <StatusBadge status={ticket.status} />
                  </div>
                  <div className="flex items-center gap-4 text-xs text-zinc-500 font-['Manrope']">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(ticket.created_at).toLocaleDateString('es-ES')}
                    </span>
                    {ticket.assigned_technician && (
                      <span className="flex items-center gap-1">
                        <Wrench className="w-3 h-3" />
                        Técnico: {ticket.assigned_technician}
                      </span>
                    )}
                  </div>
                  {ticket.resolution_notes && (
                    <div className="mt-3 pt-3 border-t border-white/5">
                      <p className="text-xs text-zinc-400"><span className="text-zinc-500">Notas:</span> {ticket.resolution_notes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Resolved Tickets - Historial mejorado */}
        {resolvedTickets.length > 0 && (
          <div className="mb-8">
            <h3 className="text-xs uppercase tracking-wider text-zinc-500 mb-4 font-['Manrope']">Historial de solicitudes</h3>
            <div className="space-y-3">
              {resolvedTickets.slice(0, 5).map(ticket => {
                // Determine if it's maintenance or incident
                const isIncident = ticket.type === 'incident';
                const isMaintenance = ticket.type === 'maintenance' || ticket.type === 'revision';
                const statusText = isIncident 
                  ? '✅ Incidencia resuelta' 
                  : isMaintenance 
                    ? '✅ Mantenimiento completado' 
                    : '✅ Solicitud completada';
                
                return (
                  <div key={ticket.id} className="p-4 rounded-xl bg-emerald-500/[0.03] border border-emerald-500/10">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-emerald-400" />
                        <span className="text-sm text-emerald-400 font-medium font-['Outfit']">{statusText}</span>
                      </div>
                      <span className="text-xs text-zinc-500 font-['Manrope']">
                        {new Date(ticket.resolved_at || ticket.created_at).toLocaleDateString('es-ES', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                    <div className="ml-8">
                      <p className="text-sm text-zinc-300 font-['Manrope']">{ticket.description}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="font-mono text-xs text-zinc-600">{ticket.ticket_number}</span>
                        <span className="text-xs text-zinc-600">•</span>
                        <span className="text-xs text-zinc-500">{TYPE_CONFIG[ticket.type]?.label}</span>
                      </div>
                      {ticket.resolution_notes && (
                        <div className="mt-2 p-2 rounded bg-white/[0.02]">
                          <p className="text-xs text-zinc-400 font-['Manrope']">
                            <span className="text-zinc-500">Resolución:</span> {ticket.resolution_notes}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Warranty Info */}
        <div className="p-4 rounded-xl bg-emerald-500/[0.08] border border-emerald-500/20 mb-6">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-emerald-400 font-medium mb-1 font-['Outfit']">Garantía Manklar</p>
              <p className="text-xs text-zinc-400 font-['Manrope']">
                Si comunicas una avería y no queda resuelta en un plazo máximo de <span className="text-white font-medium">48 horas laborables</span>, 
                Manklar abonará la siguiente mensualidad del equipo afectado.
              </p>
            </div>
          </div>
        </div>

        {/* WhatsApp Contact */}
        <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 text-center">
          <h3 className="text-white font-medium mb-2 font-['Outfit']">¿Necesitas ayuda urgente?</h3>
          <p className="text-sm text-zinc-400 mb-4 font-['Manrope']">Contacta directamente con nuestro equipo técnico</p>
          <Button 
            onClick={handleContactWhatsApp}
            className="bg-[#25D366] hover:bg-[#20BD5A] text-white rounded-xl shadow-lg shadow-[#25D366]/20"
            data-testid="whatsapp-contact-btn"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            WhatsApp: +34 623 243 967
          </Button>
        </div>
      </main>

      <ScrollToTop />
    </div>
  );
}
